import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ToastProvider } from './components/ToastProvider'
import { CrossFocusAreaProvider } from './components/CrossFocusAreaProvider'
import { RoleGuard } from './components/RoleGuard'
import { Login } from './pages/Login'
import { AzureCallback } from './pages/AzureCallback'
import { Reset } from './pages/Reset'
import AiConnect from './pages/AiConnect'
import { AppLayout } from './layouts/AppLayout'
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext'
import { TestDataVisibilityProvider } from './context/TestDataVisibilityContext'
import { useEffectiveUser } from './hooks/useEffectiveUser'
import { FADeclarationDialog } from './components/FADeclarationDialog'

// Council-side pages
import AuditQueue from './pages/AuditQueue'
import DigitalCouncilQueue from './pages/DigitalCouncilQueue'
import CouncilTriage from './pages/CouncilTriage'
import { DemandDetail } from './pages/DemandDetail'
import { DemandList } from './pages/DemandList'
import { ProductDetail } from './pages/ProductDetail'
import { ProductKanban } from './pages/ProductKanban'
import { ProductList } from './pages/ProductList'
import { SubmitDemand } from './pages/SubmitDemand'
import DemandKanban from './pages/DemandKanban'
import { FocusAreaWorkspace } from './pages/FocusAreaWorkspace'
import { TalentDirectory } from './pages/TalentDirectory'
import { TalentProfileDetail } from './pages/TalentProfileDetail'

// Talent-side pages
import { TalentDashboard } from './pages/TalentDashboard'
import { Opportunities } from './pages/Opportunities'
import { MySubmissions } from './pages/MySubmissions'

// Personal
import { Profile } from './pages/Profile'
import { Settings } from './pages/Settings'
import { Tickets } from './pages/Tickets'

// Placeholders (mockup nav entries that don't have content yet)
import { Reports } from './pages/Reports'
import { Help } from './pages/Help'
import { MemberHome } from './pages/MemberHome'
import { isPlainMember } from './lib/userKind'
import { WhatsNew } from './pages/WhatsNew'

// Admin-only surfaces
import { AdminAllUsers } from './pages/AdminAllUsers'
import { AdminRosterHealth } from './pages/AdminRosterHealth'
import { AdminAuditLog } from './pages/AdminAuditLog'
import { AdminActivityStream } from './pages/AdminActivityStream'
import { AdminAnnouncements } from './pages/AdminAnnouncements'
import { AdminEmailLog } from './pages/AdminEmailLog'
import { AdminBroadcastFunnel } from './pages/AdminBroadcastFunnel'
import { AdminNotificationCatalog } from './pages/AdminNotificationCatalog'
import { AdminMondayMail } from './pages/AdminMondayMail'
import { AdminPlanning } from './pages/AdminPlanning'
import { PlanningPresent } from './pages/PlanningPresent'

/**
 * DACcelerate v2 — router shell.
 *
 * Council users land on Triage / FA Workspace (when those land). Talent
 * users land on their dashboard. Currently the home is a per-role
 * placeholder that routes to the right landing.
 */

function DigitalCouncilRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/dsai${search}`} replace />
}

function HomeRouter() {
  const user = useEffectiveUser()
  const { target } = useImpersonation()
  if (user?.views_all_demands) return <Navigate to="/focus-areas" replace />
  // A member without a talent profile came to ask for something, not to be
  // staffed: no focus-area declaration (it is about which OPPORTUNITIES they
  // want to see) and no talent dashboard. The basic-user split (#1336) made
  // that call for the sidebar and the profile and missed this route (#1374).
  if (isPlainMember(user)) {
    return <MemberHome />
  }
  if (user?.role === 'talent' || user?.role === 'member') {
    // Talents/members are gated on their self-service preferred FAs, not the
    // admin-managed focus_area_code (#781).
    if (!user.preferred_focus_areas?.length) {
      if (target) return <TalentDashboard />
      return <FADeclarationDialog mode="talent" redirectTo="/" />
    }
    return <TalentDashboard />
  }
  // Admins see everything — skip FA declaration, go straight to overview
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    return <Navigate to="/focus-areas" replace />
  }
  // Council → if no FA declared, prompt them to choose one
  // (but skip the onboarding dialog when impersonating — just show the FA picker)
  const faCode = user?.focus_area_code
  if (!faCode) {
    // LT viewers are council-with-no-FA ON PURPOSE (ticket #18: review-only
    // leadership). Forcing the pick-your-FA dialog on them would hand them
    // edit rights over whichever FA they clicked — route them to the same
    // overview landing admins get instead. The same once someone has answered
    // "I do not lead one focus area": asking on every visit is how a no-FA
    // council ended up claiming FA4 (ticket #27).
    if (target || user?.views_all_demands || user?.onboarding_completed_at)
      return <Navigate to="/focus-areas" replace />
    return <FADeclarationDialog mode="council" />
  }
  return <Navigate to={`/focus-areas/${faCode}`} replace />
}

const COUNCIL_ROLES = ['council', 'admin', 'super_admin'] as const
const ADMIN_ROLES = ['admin', 'super_admin'] as const

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <CrossFocusAreaProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AzureCallback />} />
          <Route path="/reset" element={<Reset />} />

          {/* Authenticated but STANDALONE (no app sidebar) — a focused,
              shareable submission page with its own header + a link into the app. */}
          <Route
            element={
              <ProtectedRoute>
                <TestDataVisibilityProvider>
                  <ImpersonationProvider>
                    <Outlet />
                  </ImpersonationProvider>
                </TestDataVisibilityProvider>
              </ProtectedRoute>
            }
          >
            <Route path="/submit-demand" element={<SubmitDemand />} />
            {/* The browser half of connecting a local AI client (AI access). */}
            <Route path="/ai-connect" element={<AiConnect />} />
            {/* The planning page one section at a time, for sharing a screen. */}
            <Route
              path="/admin/planning/present"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <PlanningPresent />
                </RoleGuard>
              }
            />
          </Route>

          <Route
            element={
              <ProtectedRoute>
                <TestDataVisibilityProvider>
                  <ImpersonationProvider>
                    <AppLayout />
                  </ImpersonationProvider>
                </TestDataVisibilityProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<HomeRouter />} />

            {/* Council-side */}
            <Route
              path="focus-areas"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]} allowReadAll>
                  <FocusAreaWorkspace />
                </RoleGuard>
              }
            />
            <Route
              path="focus-areas/:code"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]} allowReadAll>
                  <FocusAreaWorkspace />
                </RoleGuard>
              }
            />
            <Route
              path="triage"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <CouncilTriage />
                </RoleGuard>
              }
            />
            <Route
              path="audit-queue"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <AuditQueue />
                </RoleGuard>
              }
            />
            <Route
              path="demands"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]} allowReadAll>
                  <DemandList />
                </RoleGuard>
              }
            />
            <Route
              path="demands/kanban"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]} allowReadAll>
                  <DemandKanban />
                </RoleGuard>
              }
            />
            <Route path="demand/:id" element={<DemandDetail />} />
            {/* Product pipeline (v3.0.0). Kanban ships in a later slice. */}
            <Route
              path="products"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <ProductList />
                </RoleGuard>
              }
            />
            <Route
              path="products/kanban"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <ProductKanban />
                </RoleGuard>
              }
            />
            <Route
              path="product/:id"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <ProductDetail />
                </RoleGuard>
              }
            />
            <Route path="my-submissions" element={<MySubmissions />} />
            {/* No RoleGuard: its audience is ordinary members on the Digital
                Council roster, and the server (not the client) decides who
                that is. A role guard here would bounce exactly the people the
                page is for. */}
            {/* The page is called DSAI now (2026-09-21). The old address is in
                bookmarks and in links people were sent, so it keeps working —
                carrying its ?request= with it. */}
            <Route path="dsai" element={<DigitalCouncilQueue />} />
            <Route path="digital-council" element={<DigitalCouncilRedirect />} />
            <Route
              path="talents"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <TalentDirectory />
                </RoleGuard>
              }
            />
            <Route
              path="talents/:id"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <TalentProfileDetail />
                </RoleGuard>
              }
            />

            {/* Talent-side */}
            <Route path="opportunities" element={<Opportunities />} />

            {/* Personal */}
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
            {/* Support tickets — open to every signed-in user */}
            <Route path="tickets" element={<Tickets />} />
            {/* Ticket notifications and their emails link to /tickets/{id};
                without this they fell through to the catch-all below and
                landed the reader on their home page. */}
            <Route path="tickets/:id" element={<Tickets />} />

            {/* Placeholders (mockup nav entries pending real content) */}
            <Route
              path="reports"
              element={
                <RoleGuard roles={[...COUNCIL_ROLES]}>
                  <Reports />
                </RoleGuard>
              }
            />
            <Route path="help" element={<Help />} />
            {/* What's new — release highlights, open to every signed-in user */}
            <Route path="whats-new" element={<WhatsNew />} />

            {/* Admin-only */}
            <Route
              path="admin/users"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminAllUsers />
                </RoleGuard>
              }
            />
            <Route
              path="admin/roster-health"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminRosterHealth />
                </RoleGuard>
              }
            />
            <Route
              path="admin/audit-log"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminAuditLog />
                </RoleGuard>
              }
            />
            <Route
              path="admin/activity"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminActivityStream />
                </RoleGuard>
              }
            />
            <Route
              path="admin/email-log"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminEmailLog />
                </RoleGuard>
              }
            />
            <Route
              path="admin/posting-results"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminBroadcastFunnel />
                </RoleGuard>
              }
            />
            <Route
              path="admin/notifications"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminNotificationCatalog />
                </RoleGuard>
              }
            />
            <Route
              path="admin/monday-mail"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminMondayMail />
                </RoleGuard>
              }
            />
            <Route
              path="admin/announcements"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminAnnouncements />
                </RoleGuard>
              }
            />
            <Route
              path="admin/planning"
              element={
                <RoleGuard roles={[...ADMIN_ROLES]}>
                  <AdminPlanning />
                </RoleGuard>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </CrossFocusAreaProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}
